/**
 * dB envelope (-50..0) of a recording made in the browser, in the shape the mobile recorder
 * stores as `FileMessageContent.metering`, so a freshly recorded message previews with the same
 * waveform the sent one will draw. Loudness is relative to the recording's own peak - a quiet
 * voice still fills the bars instead of flat-lining.
 */
const DB_FLOOR = -50;
const SILENCE = 1e-6;

export const computeAudioMetering = async (blob: Blob, points = 52): Promise<number[]> => {
  const context = new AudioContext();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    const samples = buffer.getChannelData(0);
    const windowSize = Math.max(1, Math.floor(samples.length / points));

    const decibels = Array.from({ length: points }, (_, i) => {
      const start = i * windowSize;
      const end = Math.min(start + windowSize, samples.length);
      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += samples[j]! * samples[j]!;
      }
      const rms = Math.sqrt(sum / Math.max(end - start, 1));
      return 20 * Math.log10(Math.max(rms, SILENCE));
    });

    const peak = Math.max(...decibels);
    return decibels.map((value) => Math.max(DB_FLOOR, Math.min(0, value - peak)));
  } finally {
    void context.close();
  }
};
